import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AppState } from '@src/providers/AppStateProvider/types';
import type { Security, StringSelectItem } from '@talos/kyoko';
import {
  SecurityTypesUtils,
  SelectorField,
  SyntheticProductTypeEnum,
  isExpired,
  isNumericText,
  logger,
  type MarketDataSnapshot,
  type MarketDataStatistics,
  type SecurityTypes,
  type SelectItem,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import type { MultilegTabData } from '../../providers/MarketTabs.types';
import {
  Butterfly,
  CFD,
  CalendarSpread,
  Future,
  OPTION_SIDES,
  Option,
  Perp,
  Spot,
  Straddle,
  Strangle,
  VerticalSpread,
  type OptionStrategy,
} from '../OMS/NewOrder/models';
import { initialRefDataState } from '../OMS/OMSReferenceDataSlice';
import type { OMSReferenceDataState } from '../OMS/types';
import { applyNewMultilegDefaulting, type NewMultilegDefaultingContext } from './defaulting/NewMultilegDefaulting';
import { MultilegComboType, OptionStrategies, isMultilegOptionComboType } from './enums';
import { LegsContainer, type Leg } from './models';
import type {
  CustomLegRow,
  EditableProperties,
  GroupableSelectItem,
  MultilegComboPanelState,
  MultilegComboState,
  MultilegPrimePayload,
  OptionStrategyRow,
} from './types';

const supportedInstruments: GroupableSelectItem<MultilegComboType>[] = [
  {
    value: MultilegComboType.Delta1Spread,
    label: 'Delta 1',
    group: 'Multileg Type',
  },
  {
    value: MultilegComboType.SyntheticCross,
    label: 'Synthetic Cross',
    group: 'Multileg Type',
  },
  {
    value: MultilegComboType.OptionsCalendarSpread,
    label: 'Options Calendar Spread',
    group: 'Options Structures',
  },
  {
    value: MultilegComboType.OptionsVerticalSpread,
    label: 'Options Vertical Spread',
    group: 'Options Structures',
  },
];

export const multilegComboState: MultilegComboState = {
  referenceData: initialRefDataState,
  panels: {},
};

function createMultilegComboPanelState(): MultilegComboPanelState {
  return {
    instrumentField: new SelectorField({
      availableItems: supportedInstruments,
      value: supportedInstruments[0],
      isDisabled: true,
      idProperty: 'value',
    }),
    isLoading: false,
    isEditing: false,
    resolvedSymbol: undefined,
    statistics: [],
    marketData: [],
    editableProperties: {},
    compactOrderBook: true,
  };
}

const handleOptionStrategyChanged = (
  state: WritableDraft<MultilegComboState>,
  panelID: string,
  optionStrategyType: OptionStrategies | undefined
) => {
  const panelState = initPanelState(state, panelID);
  const existing = panelState.optionStrategy;

  switch (optionStrategyType) {
    case OptionStrategies.Strangle:
      panelState.optionStrategy = Strangle.createFromBlank(state.referenceData);
      break;
    case OptionStrategies.Straddle:
      panelState.optionStrategy = Straddle.createFromBlank(state.referenceData);
      break;
    case OptionStrategies.VerticalSpread:
      panelState.optionStrategy = VerticalSpread.createFromBlank(state.referenceData);
      break;
    case OptionStrategies.CalendarSpread:
      panelState.optionStrategy = CalendarSpread.createFromBlank(state.referenceData);
      break;
    case OptionStrategies.Butterfly:
      panelState.optionStrategy = Butterfly.createFromBlank(state.referenceData);
      break;
    default:
      panelState.optionStrategy = undefined;
  }

  // Re-apply coin, exchange and market account fields if previously selected
  if (existing && panelState.optionStrategy) {
    panelState.optionStrategy = panelState.optionStrategy.updateCoin(existing.legs[0].data.coinField.value);
    panelState.optionStrategy = panelState.optionStrategy.updateExchange(existing.legs[0].data.exchangeField.value);
    panelState.optionStrategy.legs.forEach((_, index) => {
      const currentMarketAccount = existing.legs[index]?.data.marketAccountField.value;
      if (currentMarketAccount) {
        const newMarketAccount = panelState.optionStrategy!.legs[index].data.marketAccountField.availableItems.find(
          m => m.value === currentMarketAccount[0]?.value
        );
        panelState.optionStrategy = panelState.optionStrategy!.updateMarketAccount(
          newMarketAccount ? [newMarketAccount] : [],
          index
        );
      }
    });
  }
};

/**
 * Fills the first leg of an options multileg when creating from market card.
 * Uses the card's market account; coin and exchange come from the option security.
 */
function fillFirstLegFromOptionMarketCard(
  panelState: WritableDraft<MultilegComboPanelState>,
  referenceData: OMSReferenceDataState,
  security: SecurityTypes['Option'],
  legMarketAccounts?: string[]
): boolean {
  if (!panelState.optionStrategy) {
    return false;
  }
  try {
    let firstLeg = Option.createFromSecurity(referenceData, security);

    if (legMarketAccounts && legMarketAccounts.length > 0) {
      const marketAccountItems = legMarketAccounts.map(marketAccountName => ({
        value: marketAccountName,
        label:
          referenceData.marketAccounts.marketAccountsByName.get(marketAccountName)?.DisplayName ?? marketAccountName,
      }));
      firstLeg = firstLeg.updateMarketAccount(marketAccountItems);
    }

    // Replace only the first leg; keep second leg unchanged for user to fill
    const existingLegs = panelState.optionStrategy.legs;
    const secondLeg = existingLegs[1] as Option;
    panelState.optionStrategy = panelState.optionStrategy
      .setLegs([firstLeg, secondLeg])
      .updateCoin(firstLeg.data.coinField.value)
      .updateExchange(firstLeg.data.exchangeField.value)
      .updateMarketAccount(firstLeg.data.marketAccountField.value, 0)
      .updateType(firstLeg.data.typeField.value!, 1);
    return true;
  } catch (error) {
    logger.error(error as Error);
    return false;
  }
}

const hydrateOptionState = (
  state: WritableDraft<MultilegComboState>,
  panelID: string,
  multilegInstrument?: MultilegComboType,
  initiatingLegs?: boolean[],
  haircutRatios?: string[],
  resolvedSymbol?: string,
  syntheticProductType?: SyntheticProductTypeEnum,
  legMarketAccounts?: string[],
  legSymbols?: string[]
) => {
  const panelState = initPanelState(state, panelID);
  multilegComboSlice.caseReducers.updateInstrument(state, {
    type: multilegComboSlice.actions.updateInstrument.type,
    payload: {
      instrument: supportedInstruments.find(x => x.value === multilegInstrument)!,
      panelID,
    },
  });

  if (multilegInstrument) {
    const optionStrategyType =
      multilegInstrument === MultilegComboType.OptionsCalendarSpread
        ? OptionStrategies.CalendarSpread
        : OptionStrategies.VerticalSpread;
    handleOptionStrategyChanged(state, panelID, optionStrategyType);
    // Switch to 'Puts' if needed
    if (
      syntheticProductType === SyntheticProductTypeEnum.PutSpread ||
      syntheticProductType === SyntheticProductTypeEnum.PutCalendarSpread
    ) {
      panelState.optionStrategy = panelState.optionStrategy
        ?.updateType(OPTION_SIDES[1], 0)
        .updateType(OPTION_SIDES[1], 1);
    }
  } else {
    handleOptionStrategyChanged(state, panelID, undefined);
  }

  if (panelState.optionStrategy) {
    panelState.optionStrategy = panelState.optionStrategy
      .setInitiatingLegs(initiatingLegs && initiatingLegs.length > 0 ? initiatingLegs : [true, false])
      .setHaircutRatios(haircutRatios && haircutRatios.length > 0 ? haircutRatios : ['0', '0']);
  }

  const symbolForFirstLeg = resolvedSymbol ?? legSymbols?.[0];
  if (symbolForFirstLeg && panelState.optionStrategy) {
    const security = state.referenceData.securities.securitiesBySymbol.get(symbolForFirstLeg);
    if (!security || security.EndTime) {
      return;
    }

    const multilegDetails = security.MultilegDetails;

    const hasMarketCardContext = (legMarketAccounts?.length ?? 0) > 0;
    if (hasMarketCardContext && SecurityTypesUtils.Option.isType(security) && !multilegDetails?.Parameters?.LegParams) {
      fillFirstLegFromOptionMarketCard(panelState, state.referenceData, security, legMarketAccounts);
      return;
    }

    if (!multilegDetails?.Parameters?.LegParams) {
      return;
    }

    const resolvedOptions: Option[] = (multilegDetails.Legs || [])
      .map(leg => state.referenceData.securities.securitiesBySymbol.get(leg.Symbol))
      .filter(SecurityTypesUtils.Option.isType)
      .map((leg, index) => {
        const desiredMarketAccount = multilegDetails.Legs?.[index].Markets?.[0]?.MarketAccount;
        const option = Option.createFromSecurity(state.referenceData, leg!);
        const marketAccount = option.data.marketAccountField.availableItems.find(
          (s: StringSelectItem) => s.value === desiredMarketAccount
        );
        return option.updateMarketAccount(marketAccount ? [marketAccount] : []);
      });

    const resolvedInitiating: boolean[] = multilegDetails?.Parameters?.LegParams?.map(p => p.Initiating) ?? [];
    const resolvedHaircutRatios: string[] =
      haircutRatios && haircutRatios.length > 0
        ? haircutRatios
        : (multilegDetails?.Parameters?.LegParams?.map(p => String(p.HaircutRatio ?? '0')) ?? []);
    panelState.optionStrategy = panelState.optionStrategy
      .setLegs(resolvedOptions)
      .setInitiatingLegs(resolvedInitiating)
      .setHaircutRatios(resolvedHaircutRatios);

    panelState.resolvedSymbol = resolvedSymbol;
    panelState.instrumentField = panelState.instrumentField.setDisabled(true);
  }
};

/**
 * Fills the first leg of a custom legs multileg (Delta1Spread, SyntheticCross) when the security
 * is a single instrument (not a multileg). Supports Option, Perp, Future, Spot.
 * The second leg remains undefined for the user to fill.
 */
function fillFirstLegFromSecurity(
  panelState: WritableDraft<MultilegComboPanelState>,
  referenceData: OMSReferenceDataState,
  security: Security,
  legMarketAccounts?: string[],
  haircutRatios?: string[],
  newMultilegDefaultingContext?: NewMultilegDefaultingContext
): boolean {
  if (!panelState.customLegs) {
    return false;
  }
  let newLeg: Leg | undefined;
  try {
    if (SecurityTypesUtils.Option.isType(security)) {
      newLeg = Option.createFromSecurity(referenceData, security);
    } else if (SecurityTypesUtils.Perp.isType(security)) {
      newLeg = Perp.createFromSecurity(referenceData, security);
    } else if (SecurityTypesUtils.Future.isType(security)) {
      newLeg = Future.createFromSecurity(referenceData, security);
    } else if (SecurityTypesUtils.CFD.isType(security)) {
      newLeg = CFD.createFromSecurity(referenceData, security);
    } else if (SecurityTypesUtils.Spot.isType(security)) {
      newLeg = Spot.createFromSecurity(referenceData, security);
    }
  } catch (error) {
    logger.error(error as Error);
  }
  if (!newLeg) {
    return false;
  }
  if ((legMarketAccounts?.length ?? 0) > 0) {
    const marketAccountItems = (legMarketAccounts ?? []).map(marketAccountName => ({
      value: marketAccountName,
      label: referenceData.marketAccounts.marketAccountsByName.get(marketAccountName)?.DisplayName ?? marketAccountName,
    }));
    newLeg = newLeg.updateMarketAccount(marketAccountItems);
  }
  const resolvedHaircutRatios = haircutRatios ?? ['0', '0'];
  const customLegs = panelState.customLegs
    .setLegs([newLeg, undefined])
    .setInitiatingLegs([true, true])
    .setHaircutRatios(resolvedHaircutRatios);
  applyNewMultilegDefaultingToPanel(panelState, customLegs, newMultilegDefaultingContext);
  panelState.resolvedSymbol = undefined;
  panelState.instrumentField = panelState.instrumentField.setDisabled(false);
  return true;
}

function applyNewMultilegDefaultingToPanel(
  panelState: WritableDraft<MultilegComboPanelState>,
  customLegs: LegsContainer,
  newMultilegDefaultingContext?: NewMultilegDefaultingContext
) {
  if (!newMultilegDefaultingContext || panelState.defaultingApplied) {
    panelState.customLegs = customLegs;
    return;
  }

  const result = applyNewMultilegDefaulting({
    customLegs,
    ...newMultilegDefaultingContext,
  });
  panelState.customLegs = result.customLegs;
  panelState.autoOpenCell = result.autoOpenCell;
  panelState.defaultingApplied = result.customLegs !== customLegs;
}

const hydrateCustomLegsState = (
  state: WritableDraft<MultilegComboState>,
  panelID: string,
  instrumentType: MultilegComboType,
  haircutRatios?: string[],
  resolvedSymbol?: string,
  legMarketAccounts?: string[],
  legSymbols?: string[],
  newMultilegDefaultingContext?: NewMultilegDefaultingContext
) => {
  const panelState = initPanelState(state, panelID);
  multilegComboSlice.caseReducers.updateInstrument(state, {
    type: multilegComboSlice.actions.updateInstrument.type,
    payload: { instrument: supportedInstruments.find(x => x.value === instrumentType)!, panelID },
  });

  const symbolForFirstLeg = resolvedSymbol ?? legSymbols?.[0];
  if (symbolForFirstLeg && panelState.customLegs) {
    const security = state.referenceData.securities.securitiesBySymbol.get(symbolForFirstLeg);
    if (!security || security.EndTime) {
      return;
    }

    const hasMultilegLegs = (security.MultilegDetails?.Legs?.length ?? 0) > 0;

    if (!hasMultilegLegs) {
      fillFirstLegFromSecurity(
        panelState,
        state.referenceData,
        security,
        legMarketAccounts,
        haircutRatios,
        newMultilegDefaultingContext
      );
      return;
    }

    const resolvedLegs: Leg[] = (security.MultilegDetails?.Legs || [])
      .map(leg => state.referenceData.securities.securitiesBySymbol.get(leg.Symbol))
      .filter(sec => !!sec && !isExpired(sec))
      .map((leg, index) => {
        let newLeg: Leg;
        try {
          if (SecurityTypesUtils.Option.isType(leg)) {
            newLeg = Option.createFromSecurity(state.referenceData, leg);
          } else if (SecurityTypesUtils.Perp.isType(leg)) {
            newLeg = Perp.createFromSecurity(state.referenceData, leg);
          } else if (SecurityTypesUtils.Future.isType(leg)) {
            newLeg = Future.createFromSecurity(state.referenceData, leg);
          } else if (SecurityTypesUtils.CFD.isType(leg)) {
            newLeg = CFD.createFromSecurity(state.referenceData, leg);
          } else if (SecurityTypesUtils.Spot.isType(leg)) {
            newLeg = Spot.createFromSecurity(state.referenceData, leg);
          } else {
            newLeg = undefined;
          }
        } catch (error) {
          // we still can get exception from createFromSecurity if the security is not valid
          logger.error(error as Error);
        }
        const marketAccount = security.MultilegDetails?.Legs?.[index].Markets?.map(m => m.MarketAccount) || [];
        const marketAccountItems = marketAccount.map(marketAccountName => {
          return {
            value: marketAccountName,
            label:
              state.referenceData.marketAccounts.marketAccountsByName.get(marketAccountName)?.DisplayName ??
              marketAccountName,
          };
        });
        if (newLeg) {
          return newLeg.updateMarketAccount(marketAccountItems);
        }
        return undefined;
      });

    const resolvedInitiating: boolean[] = security.MultilegDetails?.Parameters?.LegParams?.map(p => p.Initiating) ?? [];
    const resolvedHaircutRatios: string[] =
      haircutRatios && haircutRatios.length > 0
        ? haircutRatios
        : (security.MultilegDetails?.Parameters?.LegParams?.map(p => String(p.HaircutRatio ?? '0')) ?? []);
    panelState.customLegs = panelState.customLegs
      .setLegs(resolvedLegs)
      .setInitiatingLegs(resolvedInitiating)
      .setHaircutRatios(resolvedHaircutRatios);

    panelState.resolvedSymbol = resolvedSymbol;
    panelState.instrumentField = panelState.instrumentField.setDisabled(true);
  }
};

const applyInitialState = (state: WritableDraft<MultilegComboState>, panelID: string) => {
  const panelState = initPanelState(state, panelID);
  panelState.defaultingApplied = false;
  panelState.autoOpenCell = undefined;
  panelState.customLegs = new LegsContainer(
    state.referenceData,
    [Spot.createFromBlank(state.referenceData), undefined],
    [true, true]
  );
  multilegComboSlice.caseReducers.updateInstrument(state, {
    type: multilegComboSlice.actions.updateInstrument.type,
    payload: { instrument: panelState.instrumentField.value!, panelID },
  });
  panelState.resolvedSymbol = undefined;
  panelState.instrumentField = panelState.instrumentField.setDisabled(false);
  if (
    state.referenceData.options.isReady &&
    (!state.referenceData.settings.enableOptionTrading ||
      state.referenceData.options.expirationByMarketByCurrencyIdentity.size === 0)
  ) {
    panelState.instrumentField = panelState.instrumentField.updateAvailableItems(
      supportedInstruments.filter(
        x => x.value !== MultilegComboType.OptionsCalendarSpread && x.value !== MultilegComboType.OptionsVerticalSpread
      )
    );
  }
};

const multilegComboSlice = createSlice({
  name: 'multilegCombo',
  initialState: multilegComboState,
  reducers: {
    setReferenceData: (state, action: PayloadAction<OMSReferenceDataState>) => {
      state.referenceData = action.payload;
    },
    updateInstrument: (
      state,
      action: PayloadAction<{ instrument: SelectItem<MultilegComboType>; panelID: string }>
    ) => {
      const panelState = initPanelState(state, action.payload.panelID);
      panelState.resolvedSymbol = undefined;
      panelState.isEditing = false;
      panelState.autoOpenCell = undefined;
      panelState.defaultingApplied = false;

      panelState.instrumentField = panelState.instrumentField.updateValue(action.payload.instrument);
      if (panelState.customLegs) {
        panelState.customLegs = panelState.customLegs?.updateInstrumentType(action.payload?.instrument.value);
      }

      if (action.payload?.instrument.value === MultilegComboType.Delta1Spread && panelState.customLegs) {
        const areBothLegsSpot = panelState.customLegs.legs.every(leg => leg instanceof Spot);
        if (areBothLegsSpot) {
          panelState.customLegs = panelState.customLegs.setLegs([
            panelState.customLegs.legs[0] as Leg,
            Future.createFromBlank(state.referenceData),
          ]);
        }
        return;
      }
      if (action.payload?.instrument.value === MultilegComboType.SyntheticCross && panelState.customLegs) {
        const validLegs = panelState.customLegs.legs.map(leg => {
          if (leg instanceof Spot) {
            return leg;
          }
          return Spot.createFromBlank(state.referenceData);
        });
        panelState.customLegs = panelState.customLegs.setLegs(validLegs);
        return;
      }

      const optionStrategyType =
        action.payload?.instrument.value === MultilegComboType.OptionsCalendarSpread
          ? OptionStrategies.CalendarSpread
          : OptionStrategies.VerticalSpread;
      handleOptionStrategyChanged(state, action.payload.panelID, optionStrategyType);
    },
    updateOptionStrategy: (
      state,
      action: PayloadAction<{
        panelID: string;
        optionStrategy: OptionStrategy;
        isEditableParam?: boolean;
      }>
    ) => {
      const panelState = initPanelState(state, action.payload.panelID);
      panelState.optionStrategy = action.payload.optionStrategy;
      if (!action.payload.isEditableParam) {
        // if we change anything that isn't editable while in edit mode, we exist and switch to new creation mode
        panelState.isEditing = false;
        panelState.resolvedSymbol = undefined;
        panelState.editableProperties = {};
      }
    },
    updateCustomLegs: (
      state,
      action: PayloadAction<{
        panelID: string;
        customLegs: LegsContainer;
        isEditableParam?: boolean;
        applyNewMultilegDefaulting?: boolean;
        newMultilegDefaultingContext?: NewMultilegDefaultingContext;
      }>
    ) => {
      const panelState = initPanelState(state, action.payload.panelID);
      let customLegs = action.payload.customLegs;
      if (!action.payload.isEditableParam) {
        // if we change anything that isn't editable while in edit mode, we exist and switch to new creation mode
        panelState.isEditing = false;
        panelState.resolvedSymbol = undefined;
        panelState.editableProperties = {};
      }

      if (customLegs.instrumentType === MultilegComboType.Delta1Spread) {
        const leg1Security = customLegs.legs[0]?.security;
        const leg2Security = customLegs.legs[1]?.security;
        const mismatch = leg1Security?.BaseCurrency !== leg2Security?.BaseCurrency;
        if (leg1Security && leg2Security && mismatch) {
          customLegs = customLegs.updateSymbol(undefined, 1);
        }
      }
      if (customLegs.instrumentType === MultilegComboType.SyntheticCross) {
        const leg1Security = customLegs.legs[0]?.security;
        const leg2Security = customLegs.legs[1]?.security;
        const mismatchBase =
          leg1Security?.BaseCurrency !== leg2Security?.BaseCurrency &&
          leg1Security?.BaseCurrency !== leg2Security?.QuoteCurrency;
        const mismatchQuote =
          leg1Security?.QuoteCurrency !== leg2Security?.BaseCurrency &&
          leg1Security?.QuoteCurrency !== leg2Security?.QuoteCurrency;
        if (leg1Security && leg2Security && mismatchBase && mismatchQuote) {
          customLegs = customLegs.updateSymbol(undefined, 1);
        }
      }

      if (action.payload.applyNewMultilegDefaulting) {
        applyNewMultilegDefaultingToPanel(panelState, customLegs, action.payload.newMultilegDefaultingContext);
      } else {
        panelState.customLegs = customLegs;
      }
    },
    updateStatistics: (
      state,
      action: PayloadAction<{ panelID: string; statistics: MarketDataStatistics; legNumber: number }>
    ) => {
      const panelState = initPanelState(state, action.payload.panelID);

      if (action.payload.legNumber !== -1) {
        panelState.statistics[action.payload.legNumber - 1] = action.payload.statistics;
      }
    },
    updateMarketData: (
      state,
      action: PayloadAction<{ panelID: string; snapshot: MarketDataSnapshot; legNumber: number }>
    ) => {
      const panelState = initPanelState(state, action.payload.panelID);

      if (action.payload.legNumber !== -1) {
        panelState.marketData[action.payload.legNumber - 1] = action.payload.snapshot;
      }
    },
    initMultilegComboState: (state, { payload }: PayloadAction<string>) => {
      initPanelState(state, payload);
    },
    hydrateMultilegState: (
      state,
      {
        payload,
      }: PayloadAction<
        MultilegTabData & { panelID: string; newMultilegDefaultingContext?: NewMultilegDefaultingContext }
      >
    ) => {
      if (state.panels[payload.panelID] !== undefined) {
        applyInitialState(state, payload.panelID);
      }
      const panelState = initPanelState(state, payload.panelID);

      const {
        instrumentType,
        initiatingLegs,
        haircutRatios,
        resolvedSymbol,
        legSymbols,
        legMarketAccounts,
        compactOrderBook,
        newMultilegDefaultingContext,
      } = payload;

      panelState.compactOrderBook = compactOrderBook ?? true;

      switch (instrumentType) {
        case MultilegComboType.OptionsCalendarSpread:
        case MultilegComboType.OptionsVerticalSpread:
          hydrateOptionState(
            state,
            payload.panelID,
            instrumentType,
            initiatingLegs,
            haircutRatios,
            resolvedSymbol,
            undefined,
            legMarketAccounts,
            legSymbols
          );
          break;
        case MultilegComboType.Delta1Spread:
          hydrateCustomLegsState(
            state,
            payload.panelID,
            MultilegComboType.Delta1Spread,
            haircutRatios,
            resolvedSymbol,
            legMarketAccounts,
            legSymbols,
            newMultilegDefaultingContext
          );
          break;
        case MultilegComboType.SyntheticCross:
          hydrateCustomLegsState(
            state,
            payload.panelID,
            MultilegComboType.SyntheticCross,
            haircutRatios,
            resolvedSymbol,
            legMarketAccounts,
            legSymbols
          );
          break;
      }
    },
    // main difference between hydrate and prime is the argument type and also persisting to tab state when we prime
    // also in latest spec we always hydrate fully valid securities (else we don't persist) whereas prime can either prime a valid
    // security or it primes a new blank screen to create the multileg
    primeMultilegState: (
      state,
      { payload: { multilegType, security, syntheticProductType, panelID } }: PayloadAction<MultilegPrimePayload>
    ) => {
      const panelState = initPanelState(state, panelID);
      const instrumentType = multilegType ?? panelState.instrumentField.value?.value;

      if (!security) {
        applyInitialState(state, panelID);
        // we manually clear the below 3 properties because we otherwise carry it over when switching strategies; however in this case we want a blank state
        state.panels[panelID].optionStrategy = panelState.optionStrategy
          ?.updateCoin(undefined)
          .updateExchange(undefined)
          .updateMarketAccount(undefined);
      }

      if (instrumentType === MultilegComboType.SyntheticCross || instrumentType === MultilegComboType.Delta1Spread) {
        hydrateCustomLegsState(state, panelID, instrumentType, undefined, security?.Symbol);
      } else {
        hydrateOptionState(
          state,
          panelID,
          instrumentType,
          undefined,
          undefined,
          security?.Symbol,
          syntheticProductType
        );
      }
    },
    setIsLoading: (state, action: PayloadAction<{ isLoading: boolean; panelID: string }>) => {
      const panelState = initPanelState(state, action.payload.panelID);
      panelState.isLoading = action.payload.isLoading;
    },
    setIsEditing: (state, action: PayloadAction<{ isEditing: boolean | undefined; panelID: string }>) => {
      const panelState = initPanelState(state, action.payload.panelID);
      panelState.isEditing = action.payload.isEditing;
      panelState.editableProperties = {};
      if (action.payload.isEditing || action.payload.isEditing === undefined) {
        panelState.instrumentField = panelState.instrumentField.setDisabled(false);
        if (panelState.resolvedSymbol) {
          panelState.originalResolvedSymbolOnEdit = panelState.resolvedSymbol;
        }
        return;
      }
      panelState.instrumentField = panelState.instrumentField.setDisabled(true);
      const symbolToRestore = panelState.originalResolvedSymbolOnEdit ?? panelState.resolvedSymbol;
      if (panelState.originalResolvedSymbolOnEdit) {
        panelState.resolvedSymbol = panelState.originalResolvedSymbolOnEdit;
        panelState.originalResolvedSymbolOnEdit = undefined;
      }
      if (isMultilegOptionComboType(panelState.instrumentField.value?.value)) {
        hydrateOptionState(
          state,
          action.payload.panelID,
          panelState.instrumentField.value?.value as MultilegComboType,
          undefined,
          undefined,
          symbolToRestore
        );
        return;
      }
      hydrateCustomLegsState(
        state,
        action.payload.panelID,
        panelState.instrumentField.value!.value,
        undefined,
        symbolToRestore
      );
    },
    setResolvedSymbol: (state, action: PayloadAction<{ resolvedSymbol: string; panelID: string }>) => {
      const panelState = initPanelState(state, action.payload.panelID);
      panelState.resolvedSymbol = action.payload.resolvedSymbol;
      if (!action.payload.resolvedSymbol) {
        applyInitialState(state, action.payload.panelID);
      }
    },
    updateEditableProperty: (
      state,
      action: PayloadAction<{
        key: keyof EditableProperties;
        value: EditableProperties[keyof EditableProperties];
        panelID: string;
      }>
    ) => {
      const panelState = initPanelState(state, action.payload.panelID);
      const key = action.payload.key;
      const value = action.payload.value;
      if (key === 'minPriceIncrement' || key === 'minSizeIncrement' || key === 'maxMarketDataDepth') {
        // if not numeric then don't update
        if (isNumericText(value)) {
          panelState.editableProperties[key] = value;
        }
      } else {
        panelState.editableProperties[key] = value;
      }
    },
    setCompactOrderBook: (state, action: PayloadAction<{ compactOrderBook: boolean; panelID: string }>) => {
      const panelState = initPanelState(state, action.payload.panelID);
      panelState.compactOrderBook = action.payload.compactOrderBook;
    },
    clearAutoOpenCell: (state, action: PayloadAction<{ panelID: string }>) => {
      const panelState = initPanelState(state, action.payload.panelID);
      panelState.autoOpenCell = undefined;
    },
    clear: (state, action: PayloadAction<string>) => {
      const panelID = action.payload;
      applyInitialState(state, panelID);
    },
  },
});

export const multilegComboSliceActions = {
  setReferenceData: multilegComboSlice.actions.setReferenceData,
  updateInstrument: multilegComboSlice.actions.updateInstrument,
  updateOptionStrategy: multilegComboSlice.actions.updateOptionStrategy,
  updateCustomLegs: multilegComboSlice.actions.updateCustomLegs,
  updateStatistics: multilegComboSlice.actions.updateStatistics,
  updateMarketData: multilegComboSlice.actions.updateMarketData,
  initMultilegComboState: multilegComboSlice.actions.initMultilegComboState,
  hydrateMultilegState: multilegComboSlice.actions.hydrateMultilegState,
  primeMultilegState: multilegComboSlice.actions.primeMultilegState,
  setIsLoading: multilegComboSlice.actions.setIsLoading,
  setIsEditing: multilegComboSlice.actions.setIsEditing,
  setResolvedSymbol: multilegComboSlice.actions.setResolvedSymbol,
  updateEditableProperty: multilegComboSlice.actions.updateEditableProperty,
  setCompactOrderBook: multilegComboSlice.actions.setCompactOrderBook,
  clearAutoOpenCell: multilegComboSlice.actions.clearAutoOpenCell,
  clear: multilegComboSlice.actions.clear,
};

export const selectIsLoading = (state: AppState, panelID: string) => state.multilegCombo.panels[panelID]?.isLoading;
export const selectIsEditing = (state: AppState, panelID: string) => state.multilegCombo.panels[panelID]?.isEditing;
export const selectResolvedSymbol = (state: AppState, panelID: string) =>
  state.multilegCombo.panels[panelID]?.resolvedSymbol;
export const selectOriginalResolvedSymbolOnEdit = (state: AppState, panelID: string) =>
  state.multilegCombo.panels[panelID]?.originalResolvedSymbolOnEdit;
export const selectCompactOrderBook = (state: AppState, panelID: string) =>
  state.multilegCombo.panels[panelID]?.compactOrderBook;
export const selectAutoOpenCell = (state: AppState, panelID: string) =>
  state.multilegCombo.panels[panelID]?.autoOpenCell;
export const selectMarketData = (state: AppState, panelID: string) => state.multilegCombo.panels[panelID]?.marketData;
export const selectStatistics = (state: AppState, panelID: string) => state.multilegCombo.panels[panelID]?.statistics;
export const selectCustomLegs = (state: AppState, panelID: string) => state.multilegCombo.panels[panelID]?.customLegs;
export const selectOptionStrategy = (state: AppState, panelID: string) =>
  state.multilegCombo.panels[panelID]?.optionStrategy;
export const selectInstrumentField = (state: AppState, panelID: string) =>
  state.multilegCombo.panels[panelID]?.instrumentField;
export const selectEditableProperties = (state: AppState, panelID: string) =>
  state.multilegCombo.panels[panelID]?.editableProperties;
export const selectInstrument = (state: AppState, panelID: string) =>
  state.multilegCombo.panels[panelID]?.instrumentField.value?.value;

export const selectIsOptionsReferenceDataReady = (state: AppState) => state.multilegCombo.referenceData.options.isReady;
export const selectIsSecuritiesReferenceDataReady = (state: AppState) =>
  state.multilegCombo.referenceData.securities.isReady;

export const selectIsEntryMode = createSelector(
  selectIsEditing,
  selectIsLoading,
  selectResolvedSymbol,
  (isEditing, isLoading, resolvedSymbol) => {
    if (isLoading) {
      return false;
    }
    if (isEditing) {
      return true;
    }
    return !resolvedSymbol;
  }
);

export const selectResolvedSecurities = createSelector(
  selectCustomLegs,
  selectOptionStrategy,
  selectInstrument,
  (customLegs?: LegsContainer, optionStrategy?: OptionStrategy, instrument?: MultilegComboType) => {
    if (
      instrument === MultilegComboType.OptionsCalendarSpread ||
      instrument === MultilegComboType.OptionsVerticalSpread
    ) {
      return (optionStrategy?.legs || []).map(leg => leg.security);
    }

    return (customLegs?.legs || []).map(l => l?.security);
  }
);

/**
 * Selects the multileg security for the multileg we're viewing. If we're editing, this'll be populated by the Security from BE.
 * If we're creating a brand new multileg, this'll be undefined.
 */
const selectMultilegSecurity = createSelector(
  (state: AppState, panelID: string) => state.multilegCombo.panels[panelID]?.resolvedSymbol,
  (state: AppState) => state.referenceData.securities.securitiesBySymbol,
  (symbol, securities) => {
    if (!symbol) {
      return undefined;
    }

    return securities.get(symbol);
  }
);

export const selectIsValid = createSelector(
  selectResolvedSecurities,
  selectCustomLegs,
  selectOptionStrategy,
  selectInstrument,
  (securities, customLegs?: LegsContainer, optionStrategy?: OptionStrategy, instrument?: MultilegComboType) => {
    if (
      instrument === MultilegComboType.OptionsCalendarSpread ||
      instrument === MultilegComboType.OptionsVerticalSpread
    ) {
      const hasMarketAccounts = optionStrategy?.legs.every(leg => leg.data.marketAccountField.value.length > 0);

      return !optionStrategy || !securities?.length
        ? false
        : hasMarketAccounts && securities.filter(x => !!x).length === optionStrategy?.legs.length;
    } else {
      if (!customLegs) {
        return false;
      }

      const hasRequiredMarketAccounts = customLegs.legs.every(leg => {
        if (LegsContainer.isLegSpot(leg)) {
          return true;
        }
        return (leg?.data.marketAccountField.value.length || 0) > 0;
      });

      return hasRequiredMarketAccounts && securities.filter(x => !!x).length === customLegs?.legs.length;
    }
  }
);

export const selectOptionStrategyAsRows = createSelector(
  selectResolvedSecurities,
  selectStatistics,
  selectMarketData,
  selectOptionStrategy,
  (securities, statistics, marketData, optionStrategy?: OptionStrategy): OptionStrategyRow[] => {
    if (!optionStrategy) {
      return [];
    }

    return optionStrategy.legs.map((leg, index) => {
      const symbol = securities[index]?.Symbol;

      // When we change the security we don't want to display previous security info
      const hasUpToDateStatistics = statistics[index]?.Symbol === symbol;
      const hasUpToDateMarketData = marketData[index]?.Symbol === symbol;

      return {
        id: (index + 1).toString(),
        leg: (index + 1).toString(),
        coin: leg.data.coinField.value,
        exchange: leg.data.exchangeField.value,
        marketAccount: leg.data.marketAccountField.value,
        strike: leg.data.strikeField.value,
        type: leg.data.typeField.value,
        expiryDate: leg.data.expiryField.value,
        ratio: optionStrategy.ratios[index],
        initiating: optionStrategy.initiatingLegs[index],
        haircutRatio: optionStrategy.haircutRatios[index] ?? '0',
        underlyingPrice: hasUpToDateStatistics ? statistics[index]?.UnderlyingPrice : undefined,
        quoteCurrency: securities[index]?.QuoteCurrency,
        underlyingQuoteCurrency: securities[index]?.UnderlyingQuoteCurrency,
        currency: securities[index]?.BaseCurrency,
        bid: hasUpToDateMarketData ? marketData[index]?.Bids?.[0]?.Price : undefined,
        offer: hasUpToDateMarketData ? marketData[index]?.Offers?.[0]?.Price : undefined,
        mark: {
          MarkPriceIV: hasUpToDateStatistics ? statistics[index]?.MarkPriceIV : undefined,
          MarkPrice: hasUpToDateStatistics ? statistics[index]?.MarkPrice : undefined,
        },
        delta: hasUpToDateStatistics ? statistics[index]?.Delta : undefined,
        gamma: hasUpToDateStatistics ? statistics[index]?.Gamma : undefined,
        vega: hasUpToDateStatistics ? statistics[index]?.Vega : undefined,
        theta: hasUpToDateStatistics ? statistics[index]?.Theta : undefined,
      };
    });
  }
);

export const selectIsOptionPayoffAvailable = createSelector(
  selectIsValid,
  selectOptionStrategyAsRows,
  (isValid, rows) => {
    if (!isValid) {
      return false;
    }
    return rows.length && rows.every(row => row.bid && row.offer && row.underlyingPrice);
  }
);

export const selectCustomLegsAsRows = createSelector(
  selectResolvedSecurities,
  selectMultilegSecurity,
  selectStatistics,
  selectMarketData,
  selectCustomLegs,
  (securities, multilegSecurity, statistics, marketData, customLegs?: LegsContainer): CustomLegRow[] => {
    if (!customLegs) {
      return [];
    }

    return customLegs.legs.map((leg, index) => {
      const symbol = securities[index]?.Symbol;

      // When we change the security we don't want to display previous security info
      const hasUpToDateStatistics = statistics[index]?.Symbol === symbol;
      const hasUpToDateMarketData = marketData[index]?.Symbol === symbol;

      return {
        id: (index + 1).toString(),
        leg: (index + 1).toString(),
        productType: customLegs.getProductTypeForLeg(index) ?? '',
        symbol: customLegs.getSymbolForLeg(index),
        exchange: customLegs.getExchangeForLeg(index),
        markets: customLegs.getMarketAccountsForLeg(index),
        expiry: customLegs.getExpiryForLeg(index),
        strike: customLegs.getStrikeForLeg(index),
        type: customLegs.getTypeForLeg(index),
        initiating: customLegs.initiatingLegs[index],
        haircutRatio: customLegs.haircutRatios[index] ?? '0',
        ratio: getRatioForLeg(index, multilegSecurity),
        underlyingPrice: hasUpToDateStatistics ? statistics[index]?.UnderlyingPrice : undefined,
        displaySymbol: securities[index]?.DisplaySymbol,
        quoteCurrency: securities[index]?.QuoteCurrency,
        underlyingQuoteCurrency: securities[index]?.UnderlyingQuoteCurrency,
        currency: securities[index]?.BaseCurrency,
        bid: hasUpToDateMarketData ? marketData[index]?.Bids?.[0]?.Price : undefined,
        offer: hasUpToDateMarketData ? marketData[index]?.Offers?.[0]?.Price : undefined,
        mark: {
          MarkPriceIV: hasUpToDateStatistics ? statistics[index]?.MarkPriceIV : undefined,
          MarkPrice: hasUpToDateStatistics ? statistics[index]?.MarkPrice : undefined,
        },
        delta: hasUpToDateStatistics ? statistics[index]?.Delta : undefined,
        gamma: hasUpToDateStatistics ? statistics[index]?.Gamma : undefined,
        vega: hasUpToDateStatistics ? statistics[index]?.Vega : undefined,
        theta: hasUpToDateStatistics ? statistics[index]?.Theta : undefined,
      };
    });
  }
);

function initPanelState(state: WritableDraft<MultilegComboState>, panelID: string) {
  const panelState: WritableDraft<MultilegComboPanelState> = state.panels[panelID] ?? createMultilegComboPanelState();
  state.panels[panelID] = panelState;
  return panelState;
}

/**
 * Compute the Ratio (+1, -1) to show for a Leg. If there already exists a multileg security, then we look at the
 * Directions specified on those Legs. Otherwise if we're just building the multileg in the UI, then just default to [+1, -1]
 */
function getRatioForLeg(legIndex: number, multilegSecurity: Security | undefined): 1 | -1 {
  const legDirections = multilegSecurity?.MultilegDetails?.Legs.map(l => l.Direction) ?? ['Same', 'Opposite'];
  const legRatios = legDirections.map(dir => (dir === 'Same' ? 1 : -1));
  return legRatios.at(legIndex) ?? 1;
}

export const selectMultilegRows = createSelector(
  selectCustomLegsAsRows,
  selectOptionStrategyAsRows,
  selectInstrument,
  (customLegs, optionStrategy, instrument): { leg: string; symbol?: string; markets: string[] | undefined }[] => {
    if (instrument === MultilegComboType.Delta1Spread || instrument === MultilegComboType.SyntheticCross) {
      return customLegs.map(customLeg => ({
        leg: customLeg.leg,
        markets: customLeg.markets?.map(m => m.value),
        symbol: customLeg.symbol?.value,
      }));
    }

    return optionStrategy.map(optionStrat => ({
      leg: optionStrat.leg,
      markets: optionStrat.marketAccounts?.map(m => m.value),
    }));
  }
);

export const multilegComboSliceReducer = multilegComboSlice.reducer;
